import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { get } from "../../lib/request"
import { createClient } from "./createClientSlice"
import { toApiBrandIds } from "../../lib/utils/executiveBrandFilters"
import {
  formatClientWorldCityLabel,
  normalizeWorldCityRow,
} from "../../lib/worldCity"

function normalizeClientsList(payload) {
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.data)) return payload.data
  if (Array.isArray(payload?.clients)) return payload.clients
  return []
}

function normalizeClient(client) {
  if (!client) return null
  const worldCity = normalizeWorldCityRow(client.worldCity)
  return {
    ...client,
    worldCity,
    location: formatClientWorldCityLabel(client) || null,
  }
}
function getSavedSelectedClient() {
  try {
    const raw = localStorage.getItem("selectedClient")
    if (!raw) return null
    return JSON.parse(raw)
  } catch {
    return null
  }
}

/** Accepts legacy `userId` scalar or `{ userId, organizationId, brandIds }`. */
function normalizeFetchClientsArg(arg) {
  if (arg == null) {
    return { userId: null, organizationId: null, brandIds: [], force: false }
  }
  if (typeof arg === "number" || typeof arg === "string") {
    return { userId: arg, organizationId: null, brandIds: [], force: false }
  }
  if (typeof arg === "object") {
    const userId = arg.userId ?? arg.id ?? null
    const organizationId = arg.organizationId ?? arg.orgId ?? null
    const brandIds = toApiBrandIds(arg.brandIds ?? arg.hotelBrandIds ?? [])
    const force = Boolean(arg.force)
    return { userId, organizationId, brandIds, force }
  }
  return { userId: null, organizationId: null, brandIds: [], force: false }
}

function clientsFetchKey({ userId, organizationId, brandIds } = {}) {
  const uid = userId != null ? String(userId).trim() : ""
  const orgKey =
    organizationId != null && String(organizationId).trim() !== ""
      ? String(organizationId).trim()
      : ""
  const brandKey = Array.isArray(brandIds) ? brandIds.join(",") : ""
  return `${uid}|${orgKey}|${brandKey}`
}

function appendClientsBrandIds(endpoint, brandIds) {
  const ids = Array.isArray(brandIds) ? brandIds : []
  if (!ids.length) return endpoint
  const query = new URLSearchParams()
  ids.forEach((id) => {
    query.append("brand_ids", String(id))
  })
  const separator = endpoint.includes("?") ? "&" : "?"
  return `${endpoint}${separator}${query.toString()}`
}

function firstOrganizationIdFromPayload(payload) {
  if (payload == null) return null
  const arr = Array.isArray(payload) ? payload : [payload]
  const o = arr[0]
  if (!o || typeof o !== "object") return null
  return o.id ?? o.organizationId ?? o?.organization?.id ?? null
}

async function resolveOrganizationIdForClientsFetch(userId, explicitOrgId, getState) {
  const trimmed = explicitOrgId != null && String(explicitOrgId).trim() !== "" ? String(explicitOrgId).trim() : null
  if (trimmed) return trimmed

  const fromRedux = getState()?.organizations?.organizations?.[0]?.id
  if (fromRedux != null && String(fromRedux).trim() !== "") {
    return String(fromRedux).trim()
  }

  const fromOrgRole = getState()?.organizationRole?.organizations?.[0]?.id
  if (fromOrgRole != null && String(fromOrgRole).trim() !== "") {
    return String(fromOrgRole).trim()
  }

  if (!userId) return null

  const uid = String(userId).trim()
  const cached = resolvedOrganizationIdByUser.get(uid)
  if (cached) return cached

  const orgsPayload = await get(`organizations/users/${uid}`)
  const picked = firstOrganizationIdFromPayload(orgsPayload)
  const resolved =
    picked != null && String(picked).trim() !== "" ? String(picked).trim() : null
  if (resolved) resolvedOrganizationIdByUser.set(uid, resolved)
  return resolved
}

/** Avoid hammering organizations/users/{userId} when many client fetches lack orgId. */
const resolvedOrganizationIdByUser = new Map()

export function clearClientsOrgResolutionCache() {
  resolvedOrganizationIdByUser.clear()
}

export const fetchClientsByUserId = createAsyncThunk(
  "clients/fetchByUserId",
  async (arg, { rejectWithValue, getState }) => {
    try {
      const { userId: rawUserId, organizationId: argOrgId, brandIds } = normalizeFetchClientsArg(arg)
      const userId =
        rawUserId != null && String(rawUserId).trim() !== "" ? String(rawUserId).trim() : null

      if (!userId) {
        return rejectWithValue("User id is required to fetch clients.")
      }

      const organizationId = await resolveOrganizationIdForClientsFetch(userId, argOrgId, getState)
      // No org yet = new account with no properties, not a load failure.
      if (!organizationId) {
        return { data: [], userId, organizationId: null }
      }

      const endpoint = appendClientsBrandIds(
        `clients/users/${userId}/organizations/${organizationId}`,
        brandIds,
      )
      const data = await get(endpoint)
      return { data, userId, organizationId, brandIds }
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch clients")
    }
  },
  {
    condition: (arg, { getState }) => {
      const { userId, organizationId, brandIds, force } = normalizeFetchClientsArg(arg)
      if (force) return true
      const state = getState().clients ?? {}
      if (state.status === "loading") return false
      const uid = userId != null ? String(userId).trim() : ""
      if (!uid) return false
      const fetchKey = clientsFetchKey({ userId: uid, organizationId, brandIds })
      if (state.status === "succeeded" && state.lastFetchedKey === fetchKey) return false
      return true
    },
  },
)
export const fetchOrganizationsClients = createAsyncThunk(
  "clients/fetchOrganizationsClients",
  async ({ organizationId }, { rejectWithValue }) => {
    try {
      const response = await get(`organizations/${organizationId}/stats`)
      return response
    } catch (error) {
      return rejectWithValue(error?.message || "API failed")
    }
  },
)

// GET /api/v1/clients/organizations/{orgId}
export const fetchClientsByOrganizationId = createAsyncThunk(
  "clients/fetchByOrganizationId",
  async (organizationId, { rejectWithValue }) => {
    try {
      if (!organizationId) return rejectWithValue("organizationId is required")
      const data = await get(`clients/organizations/${organizationId}`)
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data?.clients)
            ? data.clients
            : []
      return list.map(normalizeClient).filter(Boolean)
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch organization clients")
    }
  },
)

const initialState = {
  clients: [],
  selectedClient: getSavedSelectedClient(),
  status: "idle", // idle | loading | succeeded | failed
  error: null,
  lastFetchedUserId: null,
  lastFetchedOrganizationId: null,
  lastFetchedKey: null,
  organizationClients: [],
  organizationClientsStatus: "idle",
  organizationClientsError: null,
}


const clientsSlice = createSlice({
  name: "clients",
  initialState,
  reducers: {
    setSelectedClient: (state, action) => {
      const client = action.payload
      if (!client) return
      const formatted = normalizeClient(client)
      if (!formatted) return
      state.selectedClient = formatted
      try {
        localStorage.setItem("selectedClient", JSON.stringify(formatted))
      } catch (_) {}
    },



    clearClients: (state) => {
      state.clients = []
      state.selectedClient = null
      state.status = "idle"
      state.error = null
      state.lastFetchedUserId = null
      state.lastFetchedOrganizationId = null
      state.lastFetchedKey = null
      try { localStorage.removeItem("selectedClient") } catch (_) {}
    },
    clearClientsError: (state) => {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClientsByUserId.pending, (state) => {
        // Keep status as succeeded when a list already exists so a background
        // refresh (force:true after add-account) does not unmount the app.
        if (!state.clients?.length) {
          state.status = "loading"
        }
        state.error = null
      })
      .addCase(fetchClientsByUserId.fulfilled, (state, action) => {
        state.status = "succeeded"
        const payload = action.payload
        const raw =
          payload && typeof payload === "object" && "data" in payload
            ? payload.data
            : payload
        const list = normalizeClientsList(raw)
        state.clients = list.map(normalizeClient).filter(Boolean)
        if (payload?.userId != null) {
          state.lastFetchedUserId = String(payload.userId)
        }
        if (payload?.organizationId != null) {
          state.lastFetchedOrganizationId = String(payload.organizationId)
        }
        state.lastFetchedKey = clientsFetchKey({
          userId: payload?.userId,
          organizationId: payload?.organizationId,
          brandIds: payload?.brandIds,
        })

        const clientKey = (c) => String(c?.id ?? "")

        let onboardingRaw = null
        try {
          if (typeof localStorage !== "undefined") {
            onboardingRaw = localStorage.getItem("onboarding_client_id")
          }
        } catch (_) {
          /* ignore */
        }
        const onboardingKey =
          onboardingRaw != null && String(onboardingRaw).trim() !== ""
            ? String(onboardingRaw).trim()
            : null

        const onboardingMatch =
          onboardingKey &&
          state.clients.find((c) => clientKey(c) === onboardingKey)

        if (onboardingMatch) {
          state.selectedClient = normalizeClient(onboardingMatch)
        } else {
          const currentId = state.selectedClient?.id
          const currentKey = currentId != null && currentId !== "" ? String(currentId) : null
          const matched =
            currentKey &&
            state.clients.find((c) => clientKey(c) === currentKey)

          if (matched) {
            state.selectedClient = normalizeClient(matched)
          } else if (state.clients.length > 0) {
            state.selectedClient = normalizeClient(state.clients[0])
          } else {
            state.selectedClient = null
          }
        }
        if (state.selectedClient) {
          try {
            localStorage.setItem("selectedClient", JSON.stringify(state.selectedClient))
          } catch (_) {}
        }
      })


      .addCase(fetchClientsByUserId.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.payload || "Failed to fetch clients"
      })
      .addCase(createClient.fulfilled, (state, action) => {
        const raw = action.payload?.data ?? action.payload?.client ?? action.payload
        if (!raw || typeof raw !== "object") return
        const created = normalizeClient({
          ...raw,
          id: raw.id ?? raw.clientId,
        })
        if (!created?.id && created?.id !== 0) return
        const key = String(created.id)
        if (!state.clients.some((client) => String(client?.id ?? "") === key)) {
          state.clients = [...state.clients, created]
        }
        state.selectedClient = created
        state.status = "succeeded"
        state.error = null
        try {
          localStorage.setItem("selectedClient", JSON.stringify(created))
        } catch {
          /* ignore */
        }
      })
      .addCase(fetchClientsByOrganizationId.pending, (state) => {
        state.organizationClientsStatus = "loading"
        state.organizationClientsError = null
      })
      .addCase(fetchClientsByOrganizationId.fulfilled, (state, action) => {
        state.organizationClientsStatus = "succeeded"
        state.organizationClients = action.payload ?? []
      })
      .addCase(fetchClientsByOrganizationId.rejected, (state, action) => {
        state.organizationClientsStatus = "failed"
        state.organizationClientsError =
          action.payload || "Failed to fetch organization clients"
      })
  },
})

export const { setSelectedClient, clearClients, clearClientsError } = clientsSlice.actions

export default clientsSlice.reducer
